// NATIVE-EXITS-1 — one door out of the app, and a way back in.
//
// THE BUG. In a browser, `window.location.href = url` and
// `window.open(url, '_blank')` are fine: the user gets a new tab or a back
// button. In the Capacitor shell there is neither. A location assignment
// REPLACES the app with the destination and there is no back button and no tab
// bar to return with, and window.open is silently dropped. The merchant is
// stranded and has to force-quit.
//
// THE FIX. On native, every external destination opens in a system browser
// sheet ON TOP of the app, with its own Done button. The app is still running
// underneath and is still there when the sheet closes.
//
// ONE HELPER, SO NO CALL SITE DECIDES FOR ITSELF. A guard scans for raw
// window.location.href / window.open on external URLs, so a future link cannot
// be added the old way without failing the suite.
//
// ON THE WEB NOTHING CHANGES. Both modes fall through to exactly the call they
// replaced, which is why routing a public customer page through this helper
// would be a no-op for the customer paying an invoice in Safari.

import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'

/**
 * How the destination behaves ON THE WEB. Native ignores this: a sheet is a
 * sheet either way.
 *
 *   'redirect'  replaces the current page (OAuth handoffs, hosted onboarding)
 *   'tab'       opens a new tab (PDFs, signed media, statements)
 *
 * Keeping the distinction is what makes the web path byte-identical to what it
 * replaced, rather than "close enough".
 */
export type ExternalMode = 'redirect' | 'tab'

/**
 * NATIVE-SCHEME-2 — poll and close.
 *
 * Whop is handed `redirect_url` directly and performs the redirect itself, and
 * whether it accepts a custom scheme is undocumented. Rather than test that
 * against the live payment path, the APP closes the sheet itself the moment
 * its own data says paid. No provider dependency, no server change — and it
 * fits the data-decides rule better than a scheme would, because the sheet
 * closes BECAUSE the data says paid, not because a url fired.
 */
export interface CheckoutWatch {
  /**
   * Re-reads the page's own state and answers "settled yet?".
   *
   * The page supplies this using ITS OWN existing loader, so there is exactly
   * one fetch path and one paid predicate. The watcher never queries anything
   * by itself.
   *
   * Must not throw: a failed poll is a nothing-happened, not an error.
   */
  isSettled: () => Promise<boolean>
}

/**
 * HOW OFTEN, AND FOR HOW LONG. These are not the NATIVE-EXITS-3 numbers and
 * must not be: that poll runs AFTER a return and only waits out a webhook, so
 * 5 tries at 2s is right. THIS one waits through a human being typing card
 * details on a phone.
 *
 * 3s cadence: a real customer takes 30 to 120 seconds to enter a card, and 3s
 * means the sheet closes within about three seconds of the payment landing,
 * which reads as instant. Faster would be chattier for no perceptible gain.
 *
 * 3 minute ceiling: covers the slow end of that range with real headroom (a
 * customer hunting for their wallet, a 3-D Secure step) and then stops. It is
 * a DEADLINE, not an attempt count, so a suspended timer that resumes cannot
 * quietly extend the window.
 */
const CHECKOUT_POLL_INTERVAL_MS = 3_000
const CHECKOUT_POLL_CEILING_MS = 180_000

export interface OpenExternalOptions {
  mode?: ExternalMode
  /**
   * Native only. Poll while the sheet is open and CLOSE IT when settled.
   *
   * Closing runs the same `browserFinished` path a manual Done runs, so both
   * converge on onReturn and there is one return code path, not two.
   */
  watch?: CheckoutWatch
  /**
   * NATIVE ONLY, and the reason this wave is more than a plugin swap.
   *
   * On the web these flows come back by REDIRECTING to the app with a query
   * param (?onboarding=complete, ?connected=1), and the app keys its recovery
   * off that param. In a sheet the provider's redirect lands INSIDE THE SHEET,
   * so the app never sees the param and never refreshes: the merchant returns
   * to stale state and concludes the flow failed.
   *
   * onReturn fires when the sheet closes and is where a call site re-runs
   * whatever the query param would have triggered.
   */
  onReturn?: () => void
}

export function isNativeShell(): boolean {
  return Capacitor.isNativePlatform()
}

/**
 * Open an EXTERNAL destination.
 *
 * Never throws: a failure to open a browser must not take down the screen that
 * asked for it. It falls back to the web behaviour, which on native is a last
 * resort but is better than nothing happening at all.
 */
export async function openExternal(url: string, opts: OpenExternalOptions = {}): Promise<void> {
  const mode: ExternalMode = opts.mode ?? 'redirect'

  if (!isNativeShell()) {
    if (mode === 'tab') window.open(url, '_blank', 'noopener,noreferrer')
    else window.location.href = url
    return
  }

  try {
    // NATIVE-SCHEME-2: stop the watcher on close, whichever way it closed.
    let stopWatch: (() => void) | undefined

    // One-shot listener: it must not survive the sheet, or a later unrelated
    // close would re-fire a call site's refresh.
    if (opts.onReturn || opts.watch) {
      const handle = await Browser.addListener('browserFinished', () => {
        void handle.remove()
        stopWatch?.()
        opts.onReturn?.()
      })
    }

    if (opts.watch) stopWatch = startCheckoutWatch(opts.watch)

    await Browser.open({ url, presentationStyle: 'popover' })
  } catch {
    // The plugin failed (not installed on this platform, malformed URL). Fall
    // back so the action still does something.
    if (mode === 'tab') window.open(url, '_blank', 'noopener,noreferrer')
    else window.location.href = url
  }
}

/**
 * The default return handler for an account-changing handoff.
 *
 * WHY A RELOAD RATHER THAN A TARGETED REFETCH. On the WEB these flows already
 * return by a full page load: the provider redirects the browser back to the
 * app, every loader re-runs, and nothing can be stale. In the shell the sheet
 * closes onto a still-running app whose state predates the flow entirely — a
 * new payout account, a connected calendar, a completed verification.
 *
 * Reloading reproduces the web return exactly, which is both the smallest
 * change and the most faithful one: no screen has to enumerate which of its
 * queries the flow might have invalidated, and no screen can get that list
 * wrong. Screens that need something SMARTER than a reload pass their own
 * onReturn instead — Payments does, because its verification webhook lags the
 * return by 5 to 15 seconds and it must re-enter a poll rather than refetch
 * once.
 */
export function reloadOnReturn(): void {
  if (!isNativeShell()) return
  window.location.reload()
}

// ── NATIVE-SCHEME-1: the sheet dismisses itself ───────────────────────────
//
// WHAT WAS WRONG. After a flow finished, its confirmation rendered INSIDE the
// sheet and the merchant had to tap Done before the app underneath saw
// anything. On an OAuth return they watched the WEB app load in a sheet first.
//
// HOW IT WORKS, AND WHY NO PROVIDER HAD TO AGREE TO ANYTHING. For OAuth the
// provider's registered redirect_uri is the EDGE FUNCTION, not this app. The
// function then 302s to `return_url`, a value WE store and WE control, with no
// validation on it. So the scheme only ever travels between our own callback
// and our own app; Meta and Google never see it and nothing in their
// dashboards changes.
//
// THE WEB IS UNTOUCHED. nativeReturnUrl returns the https url it was given
// unless the caller is the native shell, so a browser gets byte-identical
// behaviour to today.

/** The app's registered scheme. Must match CFBundleURLSchemes in Info.plist. */
export const NATIVE_SCHEME = 'ghostcrm'

/**
 * The return url to hand a flow.
 *
 * NOTHING SENSITIVE TRAVELS IN IT. The only parameter is `to`, an in-app path
 * such as /dashboard/settings/connections. No token, no id, no account
 * identifier: the callback already holds those server-side keyed by its state
 * nonce, and a custom scheme can be claimed by any app that registers it, so
 * anything put here should be treated as public.
 */
export function nativeReturnUrl(webUrl: string, inAppPath: string): string {
  if (!isNativeShell()) return webUrl
  return `${NATIVE_SCHEME}://oauth-return?to=${encodeURIComponent(inAppPath)}`
}

/**
 * Close the sheet and hand the return back to the app.
 *
 * Registered once, at app start. Returns a disposer.
 *
 * IT STILL CLAIMS NOTHING. This changes WHEN the sheet closes, never WHAT the
 * app believes. The callback's own query (?connected=1, ?error=...) is
 * forwarded onto the in-app path so the existing handlers run exactly as they
 * do on the web, and every paid/connected claim stays keyed to real data.
 */
export async function registerNativeReturnHandler(
  navigate: (pathWithQuery: string) => void,
): Promise<() => void> {
  if (!isNativeShell()) return () => {}
  const { App } = await import('@capacitor/app')
  const handle = await App.addListener('appUrlOpen', ({ url }) => {
    if (!url?.startsWith(`${NATIVE_SCHEME}://`)) return
    void Browser.close().catch(() => {})
    try {
      const parsed = new URL(url)
      const to = parsed.searchParams.get('to') || '/dashboard'
      parsed.searchParams.delete('to')
      const rest = parsed.searchParams.toString()
      navigate(rest ? `${to}?${rest}` : to)
    } catch {
      navigate('/dashboard')
    }
  })
  return () => void handle.remove()
}

/**
 * Poll until settled, then close the sheet. Returns a stopper.
 *
 * IT CLAIMS NOTHING. Its only effect is Browser.close(). It sets no paid
 * state, touches no screen and tells no page anything — the page learns what
 * happened the same way it always did, by reading its own data after the
 * close. This changes WHEN the sheet closes, never WHAT the app believes.
 *
 * THREE WAYS IT STOPS, and it can never run forever:
 *   settled   the sheet is closed and the chain ends
 *   closed    browserFinished stops it, including a manual Done
 *   deadline  a wall-clock ceiling ends it even if nothing else does
 *
 * IF THE APP IS BACKGROUNDED iOS suspends these timers and the watch simply
 * stops making progress. That is not a failure mode: the sheet stays open, the
 * customer taps Done, browserFinished fires and the ordinary return handling
 * runs exactly as it did before this wave. The auto-close is an accelerator,
 * never the only way home.
 */
function startCheckoutWatch(watch: CheckoutWatch): () => void {
  let stopped = false
  let timer: number | undefined
  const deadline = Date.now() + CHECKOUT_POLL_CEILING_MS

  const stop = () => {
    stopped = true
    if (timer !== undefined) window.clearTimeout(timer)
  }

  const tick = () => {
    if (stopped) return
    if (Date.now() >= deadline) return stop()
    timer = window.setTimeout(() => {
      void (async () => {
        if (stopped) return
        let settled = false
        try {
          settled = await watch.isSettled()
        } catch {
          // A failed read is a nothing-happened. Keep waiting.
        }
        if (stopped) return
        if (settled) {
          stop()
          await Browser.close().catch(() => {})
          return
        }
        tick()
      })()
    }, CHECKOUT_POLL_INTERVAL_MS)
  }

  tick()
  return stop
}
